import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';
import { z } from 'zod';

import { FeApi, useInjectFeApiContext } from '@/api/fe-api';
import { useMe } from '@/hooks/use-me';
import type { DeepUnknown } from '@/types/deep-unknown';

import { TaskSchema } from '../entities/task';
import { WorkstreamSchema } from '../entities/workstream';

export namespace TaskQuery {
  export const key = (input: Input) => ['project-management', 'task', input.id];

  export type Input = { id: string };

  export const OutputSchema = z.object({
    task: TaskSchema.pick({ id: true, title: true }).extend({
      workstream: WorkstreamSchema.pick({ id: true, name: true }).optional(),
    }),
  });
  export type Output = z.infer<typeof OutputSchema>;

  function outputMapper(
    data: FeApi.GetCardDetailViewOutputRead,
  ): DeepUnknown<Output> {
    return {
      task: {
        id: data.card.uuid,
        title: data.card.title,
        workstream: data.workstream_project_details?.workstream
          ? {
              id: data.workstream_project_details.workstream.uuid,
              name: data.workstream_project_details.workstream.name,
            }
          : undefined,
      },
    };
  }

  function useQueryFn() {
    const me = useMe();
    const taskDetails = useInjectFeApiContext(FeApi.tasksV1CardsDetailRetrieve);

    return async function queryFn(input: Input) {
      const taskResult = await ok(
        taskDetails({
          uuid: input.id,
          customerType: me.config.type,
        }),
      );

      const output = await OutputSchema.parseAsync(outputMapper(taskResult));

      return output;
    };
  }

  export function useQuery(input: Input) {
    const queryFn = useQueryFn();

    return rq.useQuery<Output>({
      queryKey: key(input),
      queryFn: () => queryFn(input),
    });
  }

  export function usePrefetch() {
    const queryClient = rq.useQueryClient();
    const queryFn = useQueryFn();

    return async function prefetch(input: Input): Promise<void> {
      await queryClient.prefetchQuery<Output>({
        queryKey: key(input),
        queryFn: () => queryFn(input),
      });
    };
  }
}
